import merge from "lodash/merge";
import { BaselineData, BaselineSeriesOptions, ChartCandle } from "../chart.typings";
import BaseChartSeries from "./baseSeries";
import { BaseLineSeries as CustomBaselineSeries } from "../customSeries/baselineSeries/baselineSeries";
import { BaselineLastPricePulsePrimitive } from '../customSeries/baselineSeries/lastPricePulsePrimitive'
import { defaultOptions } from '../customSeries/baselineSeries/options'
import { IndicatorOutput_OutputStyle } from "../../../proto/generated/indicator";
import { evaluateExpression } from "../../../util/safeExpressionEval";
import { DeepPartial } from "lightweight-charts";

class BaselineSeries extends BaseChartSeries<BaselineData, BaselineSeriesOptions> {

    customPaneView?: CustomBaselineSeries<BaselineData>

    init() {
        this.customPaneView = new CustomBaselineSeries(this.chart);
    }

    addSeriesToChart() {
        const api = this.chart.chartApi.addCustomSeries(this.customPaneView!, this.seriesOptions)
        api.attachPrimitive(new BaselineLastPricePulsePrimitive(this.chart, this.paneIndex))
        return api
    }
    
    getDefaultOptions(): BaselineSeriesOptions {
        const t = Number(this.config.thickness) || 1
        return merge(defaultOptions, this.options, {
            lineWidth: Math.max(2, Math.round(t * 2))
        })
    }

    onOptionsUpdate(options: DeepPartial<BaselineSeriesOptions>): void {
        if ('source' in options) {
            this._setData(this.rawData, this.metadata)
        }
    }

    generateSeriesData(data: ChartCandle[], metadata: Record<string, any> = {}): BaselineData[] {
        const result: BaselineData[] = [];
        const open: number[] = new Array(data.length);
        const close: number[] = new Array(data.length);
        const series: number[] = new Array(data.length);

        const {color: seriesColor} = this.seriesApi.options()

        const {color: metaColor, lineStyle } = metadata;
        const {color: configColor} = this.config;
        const isColorHexCode = metaColor && RegExp(/^#[0-9a-fA-F]{6}([0-9a-fA-F]{2})?$/).test(metaColor)
        const color = isColorHexCode ? configColor : metaColor;
        const source = this.seriesOptions.source

        if (isColorHexCode && seriesColor !== color) {
            this.seriesApi.applyOptions({color})
        }

        // For screener charts,
        if (this.chart.screenerChart && data.length > 0 && data[0].open !== undefined) {
            const firstOpenPrice = data[0].open;
            this.seriesApi.applyOptions({ baseValue: firstOpenPrice } as any);
        }

        data.forEach((candle, i) => {
            if (candle[source] === undefined) return;
            open[i] = candle.open;
            close[i] = candle.close;
            series[i] = candle.series || 0
            let candleColor = color;
            if (isNaN(candle[source] as number) && this.config.style === IndicatorOutput_OutputStyle.WITH_BREAKS) {
                candleColor = "transparent"
            } else if (!isColorHexCode && candleColor) {
                candleColor = evaluateExpression<string>(metaColor, { open, close, series, candle, i })
            }

            result.push({
                ...candle,
                value: candle[source] as number
            })

        })

        return result;
    }

}

export default BaselineSeries;